import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Users, GraduationCap, TrendingUp, Award, Loader2, Eye, Target, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const BLOOM_COLORS = {
  Recordar:   "#94a3b8",
  Comprender: "#60a5fa",
  Aplicar:    "#34d399",
  Analizar:   "#f59e0b",
  Evaluar:    "#f87171",
  Crear:      "#a78bfa",
  "Sin nivel": "#e5e7eb",
};

const GRADE_LABELS = {
  "4": "4°", "5": "5°", "6": "6°",
  "7": "7°", "8": "8°", "9": "9°",
  "1-bach": "1° Bach", "2-bach": "2° Bach",
};

const gradeLabel = (g) => GRADE_LABELS[g] || g || "Sin grado";

/**
 * InstitutionMetricsPanel
 *
 * KPIs + gráficos + tabla de docentes con "Ver dashboard" para el
 * administrador institucional. Todos los datos vienen de:
 *   - GET /api/institution/dashboard-metrics
 *   - GET /api/institution/teacher-breakdown
 *   - GET /api/institution/teacher/:id/dashboard (modal)
 */
export default function InstitutionMetricsPanel() {
  const { user } = useContext(AuthContext);
  const [metrics, setMetrics] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherDetail, setTeacherDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/institution/dashboard-metrics`, { withCredentials: true })
          .then((r) => r.data)
          .catch((e) => { console.warn("metrics fail", e); return null; }),
        axios.get(`${API}/institution/teacher-breakdown`, { withCredentials: true })
          .then((r) => r.data?.teachers || [])
          .catch((e) => { console.warn("teachers fail", e); return []; }),
      ]);
      setMetrics(m);
      setTeachers(t);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openTeacherDashboard = async (teacher) => {
    setSelectedTeacher(teacher);
    setDetailLoading(true);
    setTeacherDetail(null);
    try {
      const r = await axios.get(
        `${API}/institution/teacher/${teacher.teacher_id}/dashboard`,
        { withCredentials: true },
      );
      setTeacherDetail(r.data);
    } catch (e) {
      console.warn("teacher dashboard fail", e);
      toast.error("No se pudo cargar el dashboard del docente");
    } finally {
      setDetailLoading(false);
    }
  };

  const bloomChartData = useMemo(() => {
    if (!metrics?.bloom_distribution) return [];
    return metrics.bloom_distribution.map((b) => ({
      level: b.level,
      count: b.count,
      color: BLOOM_COLORS[b.level] || "#cbd5e1",
    }));
  }, [metrics]);

  const gradeChartData = useMemo(() => {
    if (!metrics?.by_grade) return [];
    return metrics.by_grade.map((g) => ({
      grade: gradeLabel(g.grade),
      Estudiantes: g.students,
      "Evidencias 7d": g.evidences_last_7d,
      Puntos: g.points_total,
    }));
  }, [metrics]);

  const trendChartData = useMemo(() => {
    if (!metrics?.weekly_trend) return [];
    return metrics.weekly_trend.map((w) => ({
      week: w.week_start.slice(5),
      Evidencias: w.evidences,
      Puntos: w.points,
    }));
  }, [metrics]);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="metrics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-16 text-gray-500" data-testid="metrics-empty">
        No se pudieron cargar las métricas.
      </div>
    );
  }

  const t = metrics.totals;

  return (
    <div className="space-y-6" data-testid="institution-metrics-panel">
      {/* ── KPIs ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-kpis">
        <KpiCard icon={GraduationCap} label="Estudiantes" value={t.students} color="text-blue-600" testId="kpi-students" />
        <KpiCard icon={Users} label="Docentes" value={t.teachers} color="text-emerald-600" testId="kpi-teachers" />
        <KpiCard icon={Target} label="Evidencias últ. 7d" value={t.evidences_last_7d} color="text-amber-600" hint={`${t.evidences_total} total`} testId="kpi-evidences" />
        <KpiCard icon={Award} label="Puntos últ. 7d" value={t.points_last_7d} color="text-purple-600" hint={`${t.points_total} total`} testId="kpi-points" />
      </div>

      {/* ── Gráficos ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Por grado (bar) */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="chart-by-grade">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-gray-700">Estudiantes por grado</h3>
          </div>
          {gradeChartData.length === 0 ? (
            <p className="text-xs text-gray-500 py-10 text-center">Aún no hay estudiantes.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={gradeChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="grade" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Estudiantes" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Evidencias 7d" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Distribución Bloom (pie) */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="chart-bloom">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-purple-500" />
            <h3 className="text-sm font-semibold text-gray-700">Distribución Bloom (todas las evidencias)</h3>
          </div>
          {bloomChartData.length === 0 ? (
            <p className="text-xs text-gray-500 py-10 text-center">Aún no hay evidencias registradas.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={bloomChartData} dataKey="count" nameKey="level"
                  cx="50%" cy="50%" outerRadius={90} label={{ fontSize: 12 }}
                >
                  {bloomChartData.map((entry, idx) => (
                    <Cell key={`cell-${entry.level}-${idx}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tendencia semanal */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="chart-trend">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-semibold text-gray-700">Tendencia últimas 4 semanas</h3>
        </div>
        {trendChartData.length === 0 ? (
          <p className="text-xs text-gray-500 py-10 text-center">Aún no hay actividad reciente.</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="week" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Evidencias" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Puntos" stroke="#a78bfa" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Tabla docentes ─────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="teachers-table">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-gray-700">Docentes y su desempeño</h3>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-metrics">
            Refrescar
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Docente</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead className="text-right">Estudiantes</TableHead>
                <TableHead className="text-right">Evid. 7d</TableHead>
                <TableHead className="text-right">Puntos totales</TableHead>
                <TableHead className="text-right">Bloom prom.</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teachers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-gray-500 py-8">
                    Aún no hay docentes en esta institución.
                  </TableCell>
                </TableRow>
              ) : (
                teachers.map((tc) => (
                  <TableRow key={tc.teacher_id} data-testid={`teacher-row-${tc.username || tc.teacher_id}`}>
                    <TableCell className="font-medium text-gray-900">{tc.name}</TableCell>
                    <TableCell className="text-xs text-gray-500 font-mono">{tc.username || "—"}</TableCell>
                    <TableCell className="text-right font-semibold text-blue-600">{tc.students_assigned}</TableCell>
                    <TableCell className="text-right text-amber-600 font-medium">{tc.evidences_last_7d}</TableCell>
                    <TableCell className="text-right text-purple-600 font-medium">{tc.points_total}</TableCell>
                    <TableCell className="text-right">
                      {tc.avg_bloom_level != null ? (
                        <Badge className="bg-gray-100 text-gray-700">{tc.avg_bloom_level}</Badge>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => openTeacherDashboard(tc)}
                        data-testid={`view-teacher-dashboard-${tc.username || tc.teacher_id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> Ver dashboard
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ── Modal: dashboard del docente seleccionado ──────────────── */}
      <Dialog open={!!selectedTeacher} onOpenChange={(open) => !open && setSelectedTeacher(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="teacher-dashboard-modal">
          <DialogHeader>
            <DialogTitle>
              Dashboard docente: <span className="font-semibold">{selectedTeacher?.name}</span>
            </DialogTitle>
            <DialogDescription>
              Vista detallada de los estudiantes asignados y sus métricas de aprendizaje.
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : teacherDetail ? (
            <div className="space-y-4">
              {/* KPIs del docente */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="teacher-modal-kpis">
                <MiniKpi label="Estudiantes" value={teacherDetail.metrics.students_assigned} color="text-blue-600" />
                <MiniKpi label="Evid. totales" value={teacherDetail.metrics.evidences_total} color="text-amber-600" />
                <MiniKpi label="Evid. 7d" value={teacherDetail.metrics.evidences_last_7d} color="text-orange-600" />
                <MiniKpi label="Puntos totales" value={teacherDetail.metrics.points_total} color="text-purple-600" />
              </div>

              {/* Tabla estudiantes */}
              <div className="overflow-x-auto max-h-[400px]" data-testid="teacher-modal-students">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Estudiante</TableHead>
                      <TableHead>Grado</TableHead>
                      <TableHead className="text-right">Evid. total</TableHead>
                      <TableHead className="text-right">Evid. 7d</TableHead>
                      <TableHead className="text-right">Puntos total</TableHead>
                      <TableHead className="text-right">Puntos 7d</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teacherDetail.students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-sm text-gray-500 py-6">
                          Sin estudiantes asignados.
                        </TableCell>
                      </TableRow>
                    ) : (
                      teacherDetail.students.map((s) => (
                        <TableRow key={s.profile_id}>
                          <TableCell className="font-medium">{s.student_name}</TableCell>
                          <TableCell><Badge variant="outline">{gradeLabel(s.grade)}</Badge></TableCell>
                          <TableCell className="text-right">{s.evidences_total}</TableCell>
                          <TableCell className="text-right text-amber-600">{s.evidences_last_7d}</TableCell>
                          <TableCell className="text-right font-semibold text-purple-600">{s.points_total}</TableCell>
                          <TableCell className="text-right text-purple-500">{s.points_last_7d}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No se pudo cargar el detalle.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, hint, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${color}`} />}
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
    </div>
  );
}

function MiniKpi({ label, value, color }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}
